import { Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import moment from 'moment';
import { useEffect, useState } from 'react';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import { formatTourOptionLabel } from '../components/tourOptionLabel';

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '8px 16px 16px',
        [theme.breakpoints.down('sm')]: {
            padding: '4px 12px 8px',
            gap: '8px',
        },
        backgroundColor: '#FFFFFF',
        borderRadius: '12px 12px 0 0',
        border: '1px solid #D8D8D8',
        borderBottom: 'none',
    },
    filtersRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap',
    },
    chip: {
        borderRadius: '20px',
        height: '32px',
        '&.selected': {
            backgroundColor: '#007AFF',
            color: 'white',
        },
        '&:not(.selected)': {
            backgroundColor: 'transparent',
            border: '1px solid #D8D8D8',
            color: '#202020',
        },
    },
}));


const QUICK_FILTERS = [
    { label: 'All time', value: 'all_time' },
    { label: 'Last 90 days', value: 'last_90_days' },
    { label: 'Last year', value: 'last_year' },
];

const TourMediaListToolbar = ({
    filters,
    onFiltersChange,
    selectedCount,
    onDownloadSelected,
    onExportList,
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const [selectedTour, setSelectedTour] = useState(filters.tour_option || null);

    useEffect(() => {
        if (filters.tour_option) {
            setSelectedTour(filters.tour_option);
            return;
        }
        if (!filters.tour_id) {
            setSelectedTour(null);
        }
    }, [filters.tour_option, filters.tour_id]);

    const handleQuickFilterChange = (value) => {
        let newFilters = { ...filters, quick_filter: value };
        
        if (value === 'all_time') {
            newFilters.start_date = null;
            newFilters.end_date = null;
        } else if (value === 'last_90_days') {
            newFilters.start_date = moment().subtract(90, 'days').toDate();
            newFilters.end_date = null;
        } else if (value === 'last_year') {
            newFilters.start_date = moment().subtract(1, 'year').toDate();
            newFilters.end_date = null;
        }
        
        onFiltersChange(newFilters);
    };

    const handleDateRangeChange = (dateRange) => {
        const [start, end] = dateRange || [null, null];
        if ((start && !start.isValid()) || (end && !end.isValid())) {
            return;
        }

        onFiltersChange({ 
            ...filters, 
            start_date: start ? start.toDate() : null, 
            end_date: end ? end.toDate() : null,
            quick_filter: null 
        });
    };

    const IsoToMomentFormat = (date) => {
        if (!date) return null;
        return moment(date);
    };

    const handleTourChange = (_event, value) => {
        setSelectedTour(value);
        onFiltersChange({ ...filters, tour_id: value?.id || null, tour_option: value || null });
    };

    const formatTourLabel = (option) => formatTourOptionLabel(option, (d) => moment(d).format('MMM D, YYYY'));

    return (
        <Box className={classes.toolbar}>
            <Box className={classes.filtersRow}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateRangePicker
                        slots={{ field: SingleInputDateRangeField }}
                        name="dateRange"
                        onChange={handleDateRangeChange}
                        format="MMMM D, YYYY"
                        label="Date Range"
                        value={[IsoToMomentFormat(filters.start_date), IsoToMomentFormat(filters.end_date)]}
                        slotProps={{
                            textField: {
                                size: 'small',
                                fullWidth: true,
                                sx: {
                                    width: isDesktop ? 360 : '100%',
                                    '& .MuiInputBase-root': {
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                    '& .MuiInputBase-input': {
                                        padding: '0 14px',
                                        lineHeight: '48px',
                                    },
                                    '& .MuiInputLabel-root': {
                                        transform: 'translate(14px, 14px) scale(1)',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        transform: 'translate(14px, -8px) scale(0.75)',
                                    },
                                }
                            },
                            actionBar: {
                                actions: ['clear', 'accept'],
                            },
                        }}
                    />
                </LocalizationProvider>
                <Box sx={{ flex: 1, minWidth: isDesktop ? 260 : '100%' }}>
                    <RemoteAutocomplete
                        resource="tour"
                        label="Tour"
                        value={selectedTour}
                        onChange={handleTourChange}
                        refreshKey={filters.tour_id || 'none'}
                        apiParams={{ fields: 'id,name,is_bespoke,start_date,end_date' }}
                        getOptionLabel={formatTourLabel}
                        renderOption={(props, option) => (
                            <li {...props}>
                                {formatTourLabel(option)}
                            </li>
                        )}
                        textFieldProps={{
                            sx: {
                                '& .MuiInputBase-root': {
                                    height: '48px',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                                '& .MuiInputBase-input': {
                                    padding: '0 14px',
                                    lineHeight: '48px',
                                },
                                '& .MuiInputLabel-root': {
                                    transform: 'translate(14px, 14px) scale(1)',
                                },
                                '& .MuiInputLabel-shrink': {
                                    transform: 'translate(14px, -8px) scale(0.75)',
                                },
                            }
                        }}
                        fullWidth
                    />
                </Box>
                {isDesktop && (
                    <Box sx={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                        {QUICK_FILTERS.map((qf) => (
                            <Chip
                                key={qf.value}
                                label={qf.label}
                                onClick={() => handleQuickFilterChange(qf.value)}
                                className={`${classes.chip} ${
                                    filters.quick_filter === qf.value ? 'selected' : ''
                                }`}
                            />
                        ))}
                    </Box>
                )}
            </Box>
            {!isDesktop && (
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    {QUICK_FILTERS.map((qf) => (
                        <Chip
                            key={qf.value}
                            label={qf.label}
                            onClick={() => handleQuickFilterChange(qf.value)}
                            className={`${classes.chip} ${
                                filters.quick_filter === qf.value ? 'selected' : ''
                            }`}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default TourMediaListToolbar;

